import { useMemo, useState } from "react";
import {
  CONTENT_GOVERNANCE_CONSOLE_MODE,
  CONTENT_GOVERNANCE_CONSOLE_SECTIONS,
  CONTENT_GOVERNANCE_PUBLICATION_CHECKLIST,
  CONTENT_GOVERNANCE_ROLE_SUMMARY,
  CONTENT_GOVERNANCE_WORKFLOW,
  summarizeContentGovernanceConsole,
} from "../lib/contentGovernanceConsoleModel";

const workflowLabels: Record<string, string> = {
  draft: "Brouillon",
  submitted: "Soumis",
  in_review: "En révision",
  changes_requested: "Corrections demandées",
  approved: "Approuvé",
  scheduled: "Planifié",
  published: "Publié",
  archived: "Archivé",
  rejected: "Refusé",
};

export default function AdminContentGovernance() {
  const [domainFilter, setDomainFilter] = useState("all");
  const summary = summarizeContentGovernanceConsole();
  const sections = useMemo(() => (
    domainFilter === "all"
      ? CONTENT_GOVERNANCE_CONSOLE_SECTIONS
      : CONTENT_GOVERNANCE_CONSOLE_SECTIONS.filter(({ id }) => id === domainFilter)
  ), [domainFilter]);

  return (
    <main
      data-forfeo-content-governance-console="read-only"
      className="min-h-screen bg-slate-50 px-4 py-8 text-slate-950 sm:px-6 lg:px-8"
    >
      <div className="mx-auto w-full max-w-7xl space-y-8">
        <header className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-8">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-3xl space-y-3">
              <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-700">
                Lot 4H · Gouvernance des contenus
              </p>
              <h1 className="text-3xl font-black tracking-tight sm:text-4xl">
                Console de gouvernance éditoriale
              </h1>
              <p className="text-sm leading-6 text-slate-600 sm:text-base">
                Vue locale des catégories, régions, partenaires, articles, règles
                éditoriales et files de modération. Aucune publication n’est exécutée ici.
              </p>
            </div>
            <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
              <p className="text-xs font-black uppercase tracking-wider text-amber-900">
                Mode lecture seule
              </p>
              <p className="mt-1 text-sm text-amber-800">
                {CONTENT_GOVERNANCE_CONSOLE_MODE}
              </p>
            </div>
          </div>
        </header>

        <section aria-labelledby="h2-summary" className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <h2 id="h2-summary" className="sr-only">Résumé de la gouvernance</h2>
          {[
            ["Domaines", summary.domainCount],
            ["Domaines bilingues", summary.bilingualDomainCount],
            ["Étapes du cycle", summary.workflowStageCount],
            ["Rôles distincts", summary.roleCount],
          ].map(([label, value]) => (
            <article key={label} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <p className="text-sm font-semibold text-slate-500">{label}</p>
              <p className="mt-2 text-3xl font-black text-slate-950">{value}</p>
            </article>
          ))}
        </section>

        <section aria-labelledby="h2-domains" className="space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 id="h2-domains" className="text-2xl font-black">Domaines gouvernés</h2>
              <p className="mt-1 text-sm text-slate-600">Inventaire H1 prêt pour une future autorité serveur.</p>
            </div>
            <label className="text-sm font-bold text-slate-700">
              Filtrer le domaine
              <select
                value={domainFilter}
                onChange={(event) => setDomainFilter(event.target.value)}
                className="mt-1 block min-h-11 w-full rounded-xl border border-slate-300 bg-white px-3 sm:w-64"
              >
                <option value="all">Tous les domaines</option>
                {CONTENT_GOVERNANCE_CONSOLE_SECTIONS.map(({ id, label }) => (
                  <option key={id} value={id}>{label}</option>
                ))}
              </select>
            </label>
          </div>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" aria-live="polite">
            {sections.map((section) => (
              <article key={section.id} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <h3 className="text-lg font-black">{section.label}</h3>
                  <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-black text-emerald-800">
                    Inventaire prêt
                  </span>
                </div>
                <dl className="mt-5 grid gap-3 text-sm">
                  <div><dt className="font-bold text-slate-500">Propriétaire</dt><dd>{section.operationalOwner}</dd></div>
                  <div><dt className="font-bold text-slate-500">Autorité financière</dt><dd>{section.financialAuthority}</dd></div>
                  <div><dt className="font-bold text-slate-500">Publication bilingue</dt><dd>{section.bilingual ? "Obligatoire" : "Non applicable"}</dd></div>
                </dl>
              </article>
            ))}
          </div>
        </section>

        <section className="grid gap-6 xl:grid-cols-[1.4fr_1fr]">
          <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
            <h2 className="text-xl font-black">Cycle éditorial contrôlé</h2>
            <ol className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {CONTENT_GOVERNANCE_WORKFLOW.map(({ stage, order, final }) => (
                <li key={stage} className="rounded-xl border border-slate-200 p-4">
                  <p className="text-xs font-black uppercase tracking-wider text-blue-700">Étape {order}</p>
                  <p className="mt-1 font-bold">{workflowLabels[stage] ?? stage}</p>
                  <p className="mt-1 text-xs text-slate-500">{final ? "État terminal contrôlé" : "Transition soumise aux rôles"}</p>
                </li>
              ))}
            </ol>
          </article>

          <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
            <h2 className="text-xl font-black">Conditions avant publication</h2>
            <ul className="mt-5 space-y-3">
              {CONTENT_GOVERNANCE_PUBLICATION_CHECKLIST.map(({ id, label, required }) => (
                <li key={id} className="flex items-start gap-3 rounded-xl bg-slate-50 p-3">
                  <span aria-hidden="true" className="mt-0.5 text-emerald-700">✓</span>
                  <span className="text-sm"><strong>{label}</strong>{required ? " — obligatoire" : ""}</span>
                </li>
              ))}
            </ul>
          </article>
        </section>

        <section aria-labelledby="h2-roles" className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <h2 id="h2-roles" className="text-xl font-black">Séparation des responsabilités</h2>
          <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {CONTENT_GOVERNANCE_ROLE_SUMMARY.map(({ permission, actionCount }) => (
              <article key={permission} className="rounded-xl border border-slate-200 p-4">
                <p className="font-black">{permission}</p>
                <p className="mt-1 text-sm text-slate-600">{actionCount} actions contractuelles bornées</p>
              </article>
            ))}
          </div>
        </section>

        <footer className="rounded-2xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-950">
          Les décisions de modération et de publication resteront indisponibles jusqu’à
          l’installation d’une autorité serveur vérifiant la session, le rôle et la piste d’audit.
        </footer>
      </div>
    </main>
  );
}
